import { getSupabaseClient } from "../supabase/index.js";
import {
  attendanceDisputeClaimReasonsModule,
  billingStatementDisputeClaimReasonsModule,
  disputeAcceptReasonsModule,
  disputeRejectReasonsModule,
} from "./dispute-reference-lists.js";
import { isCancelledSessionStatus } from "./session-status.js";
import {
  defaultTenantReferenceLists,
  normalizeTenantReferenceLists,
} from "./tenant-reference-lists.js";

function asStr(v) {
  return String(v ?? "").trim();
}

/** @typedef {import('./tenant-reference-lists.js').TenantReferenceLists} TenantReferenceLists */

const DB_REASON_TO_UI_REASON = Object.freeze({
  participant: "participants_wrong",
  attendance: "attendance_wrong",
  duration: "duration_wrong",
  price: "price_wrong",
  other: "other",
});

/**
 * @param {Iterable<string>} values
 * @returns {Set<string>}
 */
function valuesToSet(values) {
  const out = new Set();
  for (const value of values) {
    const normalized = asStr(value);
    if (normalized) out.add(normalized);
  }
  return out;
}

/**
 * @param {import('./create-reference-list-module.js').ReturnType<typeof import('./create-reference-list-module.js').createReferenceListModule>} module
 * @param {Iterable<string>} dbCodes
 * @param {import('./reference-list-entries.js').ReferenceListEntry[]} configuredEntries
 */
function mapDbCodesToReferenceListUiIds(module, dbCodes, configuredEntries) {
  const used = new Set();
  const customEntries = configuredEntries.filter((row) => !row.builtin);
  const fallbackCode = customEntries.length
    ? asStr(module.mapToDbCode(customEntries[0].id))
    : "";

  for (const rawCode of dbCodes) {
    const code = asStr(rawCode);
    if (!code) continue;

    if (module.builtinIds.includes(code)) {
      used.add(code);
      continue;
    }

    if (fallbackCode && code === fallbackCode) lockCustomReferenceListEntries(configuredEntries, used);
  }

  return used;
}

/**
 * @param {Iterable<string>} dbCodes
 * @param {import('./reference-list-entries.js').ReferenceListEntry[]} configuredEntries
 */
function lockCustomReferenceListEntries(configuredEntries, used) {
  for (const row of configuredEntries) {
    if (!row.builtin) used.add(row.id);
  }
}

function mapBillingCorrectionDbCodesToUiIds(dbCodes, configuredEntries) {
  const used = new Set();
  for (const rawCode of dbCodes) {
    const code = asStr(rawCode);
    if (!code) continue;
    const uiId = DB_REASON_TO_UI_REASON[code];
    if (uiId) {
      used.add(uiId);
      if (code === "other") lockCustomReferenceListEntries(configuredEntries, used);
      continue;
    }
    if (code === "other") lockCustomReferenceListEntries(configuredEntries, used);
  }
  return used;
}

/**
 * @param {unknown} trainers
 */
function collectTrainerAbsenceTypeIds(trainers) {
  const out = new Set();
  if (!Array.isArray(trainers)) return out;
  for (const trainer of trainers) {
    const absences = Array.isArray(trainer?.absences) ? trainer.absences : [];
    for (const absence of absences) {
      const type = asStr(absence?.type);
      if (type) out.add(type);
    }
  }
  return out;
}

/**
 * @param {{
 *   sessions?: unknown[],
 *   payments?: unknown[],
 *   trainers?: unknown[],
 *   people?: unknown[],
 *   attendanceDisputes?: unknown[],
 *   billingStatementDisputes?: unknown[],
 *   billingCorrectionsActiveHeaders?: unknown[],
 *   tenantReferenceLists?: unknown,
 * }} snapshot
 * @returns {Record<keyof TenantReferenceLists, Set<string>>}
 */
export function collectReferenceListUsedIdsFromCoachDeskSnapshot(snapshot) {
  const lists = normalizeTenantReferenceLists(snapshot?.tenantReferenceLists);
  const sessions = Array.isArray(snapshot?.sessions) ? snapshot.sessions : [];
  const payments = Array.isArray(snapshot?.payments) ? snapshot.payments : [];
  const trainers = Array.isArray(snapshot?.trainers) ? snapshot.trainers : [];
  const people = Array.isArray(snapshot?.people) ? snapshot.people : [];
  const attendanceDisputes = Array.isArray(snapshot?.attendanceDisputes) ? snapshot.attendanceDisputes : [];
  const billingStatementDisputes = Array.isArray(snapshot?.billingStatementDisputes)
    ? snapshot.billingStatementDisputes
    : [];
  const billingCorrections = Array.isArray(snapshot?.billingCorrectionsActiveHeaders)
    ? snapshot.billingCorrectionsActiveHeaders
    : [];

  const sessionCancelReasons = valuesToSet(
    sessions.map((session) => session?.status).filter((status) => isCancelledSessionStatus(status))
  );

  return {
    sessionTargetGroups: valuesToSet(sessions.map((session) => session?.target_group)),
    sessionCourtFormats: valuesToSet(sessions.map((session) => session?.court_format)),
    sessionCancelReasons,
    trainerAbsenceReasons: collectTrainerAbsenceTypeIds(trainers),
    billingFrequencies: valuesToSet([
      ...trainers.map((row) => row?.billing_frequency),
      ...people.map((row) => row?.billing_frequency),
    ]),
    billingPaymentTimings: valuesToSet([
      ...trainers.map((row) => row?.billing_payment_timing),
      ...people.map((row) => row?.billing_payment_timing),
    ]),
    trainerPayoutFrequencies: valuesToSet(trainers.map((row) => row?.trainer_payout_frequency)),
    trainerPayoutMethods: valuesToSet(trainers.map((row) => row?.trainer_payout_method)),
    attendanceDisputeClaimReasons: mapDbCodesToReferenceListUiIds(
      attendanceDisputeClaimReasonsModule,
      attendanceDisputes.map((row) => row?.claim_reason_code),
      lists.attendanceDisputeClaimReasons
    ),
    billingStatementDisputeClaimReasons: mapDbCodesToReferenceListUiIds(
      billingStatementDisputeClaimReasonsModule,
      billingStatementDisputes.map((row) => row?.claim_reason_code),
      lists.billingStatementDisputeClaimReasons
    ),
    disputeAcceptReasons: mapDbCodesToReferenceListUiIds(
      disputeAcceptReasonsModule,
      attendanceDisputes.map((row) => row?.trainer_response_reason_code),
      lists.disputeAcceptReasons
    ),
    disputeRejectReasons: mapDbCodesToReferenceListUiIds(
      disputeRejectReasonsModule,
      attendanceDisputes.map((row) => row?.trainer_response_reason_code),
      lists.disputeRejectReasons
    ),
    billingCorrectionReasons: mapBillingCorrectionDbCodesToUiIds(
      billingCorrections.map((row) => row?.reason_code),
      lists.billingCorrectionReasons
    ),
    paymentEntryMethods: valuesToSet(payments.map((row) => row?.method)),
  };
}

/**
 * @param {string} tenantId
 * @param {TenantReferenceLists} [configuredLists]
 */
async function queryDistinctColumnValues(tenantId, table, column) {
  const supabase = await getSupabaseClient();
  if (!supabase) return [];

  const { data, error } = await supabase.from(table).select(column).eq("tenant_id", tenantId).limit(5000);
  if (error) {
    console.warn(`[reference-list-usage] ${table}.${column}:`, error);
    return [];
  }

  const out = new Set();
  for (const row of data || []) {
    const value = asStr(row?.[column]);
    if (value) out.add(value);
  }
  return [...out];
}

/**
 * @param {string} tenantId
 * @param {TenantReferenceLists} configuredLists
 */
async function loadTrainerAbsenceUsedIds(tenantId) {
  const supabase = await getSupabaseClient();
  if (!supabase) return new Set();

  const { data, error } = await supabase.from("trainerstunden_trainers").select("absences").eq("tenant_id", tenantId);
  if (error) {
    console.warn("[reference-list-usage] trainer absences:", error);
    return new Set();
  }
  return collectTrainerAbsenceTypeIds(data || []);
}

/**
 * @param {string} tenantId
 * @param {TenantReferenceLists} [configuredLists]
 * @returns {Promise<Record<keyof TenantReferenceLists, Set<string>>>}
 */
export async function loadReferenceListUsedIds(tenantId, configuredLists) {
  const tid = asStr(tenantId);
  const lists = normalizeTenantReferenceLists(configuredLists);
  const empty = () => defaultTenantReferenceLists();
  const keys = Object.keys(empty());
  if (!tid) {
    return Object.fromEntries(keys.map((key) => [key, new Set()]));
  }

  const [
    targetGroups,
    courtFormats,
    sessionStatuses,
    paymentMethods,
    trainerBillingFrequencies,
    trainerBillingTimings,
    trainerPayoutFrequencies,
    trainerPayoutMethods,
    peopleBillingFrequencies,
    peopleBillingTimings,
    clubPeopleBillingFrequencies,
    clubPeopleBillingTimings,
    attendanceClaims,
    attendanceResponses,
    invoiceClaims,
    correctionReasons,
    trainerAbsenceIds,
  ] = await Promise.all([
    queryDistinctColumnValues(tid, "trainerstunden_sessions", "target_group"),
    queryDistinctColumnValues(tid, "trainerstunden_sessions", "court_format"),
    queryDistinctColumnValues(tid, "trainerstunden_sessions", "status"),
    queryDistinctColumnValues(tid, "trainerstunden_payments", "method"),
    queryDistinctColumnValues(tid, "trainerstunden_trainers", "billing_frequency"),
    queryDistinctColumnValues(tid, "trainerstunden_trainers", "billing_payment_timing"),
    queryDistinctColumnValues(tid, "trainerstunden_trainers", "trainer_payout_frequency"),
    queryDistinctColumnValues(tid, "trainerstunden_trainers", "trainer_payout_method"),
    queryDistinctColumnValues(tid, "trainerstunden_people", "billing_frequency"),
    queryDistinctColumnValues(tid, "trainerstunden_people", "billing_payment_timing"),
    queryDistinctColumnValues(tid, "club_people", "billing_frequency"),
    queryDistinctColumnValues(tid, "club_people", "billing_payment_timing"),
    queryDistinctColumnValues(tid, "trainerstunden_attendance_disputes", "claim_reason_code"),
    queryDistinctColumnValues(tid, "trainerstunden_attendance_disputes", "trainer_response_reason_code"),
    queryDistinctColumnValues(tid, "trainerstunden_billing_statement_disputes", "claim_reason_code"),
    queryDistinctColumnValues(tid, "trainerstunden_billing_corrections", "reason_code"),
    loadTrainerAbsenceUsedIds(tid),
  ]);

  const sessionCancelReasons = valuesToSet(sessionStatuses.filter((status) => isCancelledSessionStatus(status)));

  return {
    sessionTargetGroups: valuesToSet(targetGroups),
    sessionCourtFormats: valuesToSet(courtFormats),
    sessionCancelReasons,
    trainerAbsenceReasons: trainerAbsenceIds,
    billingFrequencies: valuesToSet([
      ...trainerBillingFrequencies,
      ...peopleBillingFrequencies,
      ...clubPeopleBillingFrequencies,
    ]),
    billingPaymentTimings: valuesToSet([
      ...trainerBillingTimings,
      ...peopleBillingTimings,
      ...clubPeopleBillingTimings,
    ]),
    trainerPayoutFrequencies: valuesToSet(trainerPayoutFrequencies),
    trainerPayoutMethods: valuesToSet(trainerPayoutMethods),
    attendanceDisputeClaimReasons: mapDbCodesToReferenceListUiIds(
      attendanceDisputeClaimReasonsModule,
      attendanceClaims,
      lists.attendanceDisputeClaimReasons
    ),
    billingStatementDisputeClaimReasons: mapDbCodesToReferenceListUiIds(
      billingStatementDisputeClaimReasonsModule,
      invoiceClaims,
      lists.billingStatementDisputeClaimReasons
    ),
    disputeAcceptReasons: mapDbCodesToReferenceListUiIds(
      disputeAcceptReasonsModule,
      attendanceResponses,
      lists.disputeAcceptReasons
    ),
    disputeRejectReasons: mapDbCodesToReferenceListUiIds(
      disputeRejectReasonsModule,
      attendanceResponses,
      lists.disputeRejectReasons
    ),
    billingCorrectionReasons: mapBillingCorrectionDbCodesToUiIds(correctionReasons, lists.billingCorrectionReasons),
    paymentEntryMethods: valuesToSet(paymentMethods),
  };
}

/**
 * Verhindert Löschen/Umbenennen genutzter Einträge beim Speichern (Deaktivieren bleibt möglich).
 *
 * @param {TenantReferenceLists} incoming
 * @param {TenantReferenceLists} previous
 * @param {Partial<Record<keyof TenantReferenceLists, Set<string>>>} usedIdsByKey
 */
export function protectReferenceListsOnSave(incoming, previous, usedIdsByKey) {
  const defaults = defaultTenantReferenceLists();
  const base = normalizeTenantReferenceLists(incoming);
  const prev = normalizeTenantReferenceLists(previous);
  /** @type {TenantReferenceLists} */
  const out = { ...base };

  for (const key of Object.keys(defaults)) {
    const used = usedIdsByKey[key];
    if (!used?.size) continue;

    const incomingList = [...(base[key] || [])];
    const prevById = new Map((prev[key] || []).map((row) => [row.id, row]));
    const defaultById = new Map((defaults[key] || []).map((row) => [row.id, row]));

    for (const id of used) {
      const prevRow = prevById.get(id) || defaultById.get(id);
      if (!prevRow) continue;
      const idx = incomingList.findIndex((row) => row.id === id);
      if (idx < 0) {
        incomingList.push({ ...prevRow });
        continue;
      }
      incomingList[idx] = {
        ...prevRow,
        enabled: incomingList[idx].enabled,
        sort: incomingList[idx].sort ?? prevRow.sort,
        isDefault: incomingList[idx].isDefault === true,
      };
    }

    out[key] = incomingList;
  }

  return normalizeTenantReferenceLists(out);
}

/**
 * @param {keyof TenantReferenceLists} payloadKey
 * @param {string} entryId
 * @param {Partial<Record<keyof TenantReferenceLists, Set<string>>>} usedIdsByKey
 */
export function isReferenceListEntryInUse(payloadKey, entryId, usedIdsByKey) {
  const id = asStr(entryId);
  if (!id) return false;
  return Boolean(usedIdsByKey?.[payloadKey]?.has(id));
}
